import React, { useCallback } from "react";

import { RouterLink } from "@examspark/common-ui";
import { ParsedUnitObjective, Unit } from "@examspark/types";

import Modal, { ModalProps } from "./common/Modal";
import ObjectiveDetail from "./ObjectiveDetail";

type Props = Omit<
  ModalProps,
  | "isOpen"
  | "showPagination"
  | "onNext"
  | "onPrevious"
  | "isNextDisabled"
  | "isPreviousDisabled"
  | "onRequestClose"
> & {
  objectives: ParsedUnitObjective[] | null | undefined;
  unit?: Unit | null;
  showIndex?: number | undefined;
  onShowIndexChange?: (index: number | undefined) => void;
  showPagination?: boolean;
};

const ObjectiveDetailModal = ({
  objectives,
  unit,
  showIndex,
  onShowIndexChange,
  showPagination,
  ...props
}: Props) => {
  const objective = showIndex == null ? undefined : objectives?.[showIndex];

  const onPaginate = useCallback(
    (direction: 1 | -1) => {
      if (showIndex == null || objectives == null) return;
      const newIndex = showIndex + direction;
      if (newIndex < 0 || newIndex >= objectives.length) return;
      onShowIndexChange?.(newIndex);
    },
    [objectives, onShowIndexChange, showIndex],
  );

  const isNextDisabled =
    objectives == null ||
    showIndex == null ||
    showIndex >= objectives.length - 1;
  const isPreviousDisabled = showIndex == null || showIndex <= 0;

  return (
    <Modal
      title="Standard Detail"
      size="large"
      shouldCloseOnOverlayClick
      isOpen={showIndex != null && objective != null}
      showPagination={showPagination !== false}
      onNext={() => onPaginate(1)}
      onPrevious={() => onPaginate(-1)}
      isNextDisabled={isNextDisabled}
      isPreviousDisabled={isPreviousDisabled}
      onRequestClose={() => onShowIndexChange?.(undefined)}
      {...props}
      titlebarToolbar={
        <>
          <RouterLink to={`/standard/${objective?.id}`}>
            Go To Details Page
          </RouterLink>
          {props.titlebarToolbar}
        </>
      }
    >
      <Modal.PaddedBody>
        <ObjectiveDetail objective={objective} unit={unit} />
      </Modal.PaddedBody>
    </Modal>
  );
};

type SingleProps = Omit<
  Props,
  "objectives" | "showIndex" | "onShowIndexChange"
> & {
  objective: ParsedUnitObjective | undefined;
  show?: boolean;
  onShowChange?: (show: boolean) => void;
};

ObjectiveDetailModal.Single = ({
  objective,
  unit,
  show,
  onShowChange,
  ...modalProps
}: SingleProps) => {
  if (objective == null) return null;

  return (
    <ObjectiveDetailModal
      objectives={[objective]}
      showIndex={show ? 0 : undefined}
      onShowIndexChange={(i) => onShowChange?.(i !== undefined)}
      showPagination={false}
      {...modalProps}
    />
  );
};

export default ObjectiveDetailModal;
